import { Loader } from '@percent/cause-dashboard/common/components'
import styles from './InviteesListTable.module.scss'
import { Badge, Table, TableCell, TablePagination, TableProvider, TableRow } from '@percent/lemonade'
import { IamGetInvites } from '@percent/cause-dashboard/services/types/iamResponses.types'
import { useTranslation } from 'react-i18next'
import { useTranslatedRole } from '@percent/cause-dashboard/common/hooks'

interface InviteesListTableProps {
  isLoading: boolean
  data: IamGetInvites[] | null
  totalResultCount: number
  onNextPageClick: (() => void) | null
  onPreviousPageClick: (() => void) | null
}

export function InviteesListTable({
  data,
  isLoading,
  totalResultCount,
  onNextPageClick: nextPage,
  onPreviousPageClick: previousPage
}: Readonly<InviteesListTableProps>) {
  const { t } = useTranslation()
  const { translateRole } = useTranslatedRole()

  const columns = [
    { id: t('typography.userManagement.email'), isSortable: false, props: { width: '80%' } },
    { id: t('typography.userManagement.role'), isSortable: false, props: { width: '20%' } },
    { id: t('typography.userManagement.status'), isSortable: false, props: { width: 'auto' } }
  ]

  if (isLoading) {
    return <Loader />
  }

  function getStatus(expiresAtString: string) {
    const expiresAtDate = new Date(expiresAtString)
    const currentDate = new Date()
    if (expiresAtDate > currentDate) {
      return 'pending'
    } else {
      return 'removed'
    }
  }

  return (
    <TableProvider
      isLoading={isLoading}
      totalResults={totalResultCount || 0}
      columns={columns}
      emptyTableTitle={t('table.noInvitedUsers')}
      illustration="no-results"
    >
      <Table>
        <div data-testid={'invitees-table'} />
        {data?.map(({ id, email, role, expiresAt }) => (
          <TableRow key={`permissions-list-${id}`} data-testid={`row-${id}`}>
            <TableCell>
              <div className={styles.cellWrapper}>
                <span>{email}</span>
              </div>
            </TableCell>
            <TableCell dataTestId={`row-${id}-role-${role?.displayName ?? 'none'}`}>
              {role ? (
                <Badge variant="default" testId="role-badge">
                  {translateRole(role).displayName}
                </Badge>
              ) : (
                <span data-testid="role-empty">&mdash;</span>
              )}
            </TableCell>
            <TableCell dataTestId={`row-${id}-status`}>
              {getStatus(expiresAt) === 'pending' && (
                <Badge variant="default" icon="clock">
                  {t('typography.userManagement.inviteStatus.pending', { defaultMessage: 'Awaiting response' })}
                </Badge>
              )}
              {getStatus(expiresAt) === 'removed' && (
                <Badge variant="default">
                  {t('typography.userManagement.inviteStatus.removed', { defaultMessage: 'Expired' })}
                </Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </Table>
      <TablePagination
        previousPage={previousPage}
        nextPage={nextPage}
        totalResultsText={t('typography.results', { count: totalResultCount })}
      />
    </TableProvider>
  )
}
